export interface DeviceSession {
  /**
   * The unique ID of the device session.
   */
  id: string;

  /**
   * When the session was created.
   */
  createdAt: string;

  /**
   * When the session was modified.
   */
  updatedAt: string;

  /**
   * When the session was closed.
   */
  closedAt: string | null;

  /**
   * When the device was last seen.
   */
  lastSeenAt: string;

  /**
   * Duration of the session in seconds.
   */
  duration: number;

  /**
   * The ID of the device.
   */
  deviceId: string;

  /**
   * Status of the device. Can be `pending`, `open`, or `closed`.
   */
  status: 'pending' | 'open' | 'closed';
}

/**
 * Represents a client entity that a device can be attached to.
 */
export interface AttachedClient {
  /**
   * The unique ID of the client.
   */
  _id: string;

  /**
   * A human readable ID.
   */
  idAlias: string;

  /**
   * The name of the client.
   */
  name: string;
}

/**
 * Represents the basic structure of a device.
 */
export interface BaseDevice {
  /**
   * The unique ID of the device.
   */
  _id: string;

  /**
   * A human readable ID.
   */
  idAlias: string;

  /**
   * The ID of the unit this device is attached to.
   */
  unitId: string;

  /**
   * When the device got created.
   */
  createdAt: string;

  /**
   * When the device got modified.
   */
  updatedAt: string;

  /**
   * The category the device belongs to.
   */
  category: string;

  /**
   * The type of the device.
   */
  type: string;

  /**
   * The name of the device.
   */
  label: string | null;

  /**
   * The client this device is attached to, or `null` if not attached.
   */
  attachedTo: AttachedClient | null;

  /**
   * Latest session of the device or `null`.
   */
  latestSession: DeviceSession | null;
}

/**
 * Device types served by the Ivy family.
 *
 * `ivy` is the head; every other member is a rebadge that shares its metadata
 * contract, telemetry and backend handler exactly. Adding a rebadge means
 * adding a string here — the backend's `IVY_FAMILY_TYPES` is the mirror of
 * this list.
 */
export const IVY_FAMILY_TYPES = ['ivy', 'lily'] as const;

export type IvyFamilyType = (typeof IVY_FAMILY_TYPES)[number];

/**
 * Whether a device type belongs to the Ivy family.
 *
 * Use this instead of comparing against `'ivy'` directly: a rebadge carries
 * the same metadata shape, so a bare `=== 'ivy'` check silently skips it.
 */
export function isIvyFamilyType(type: string): type is IvyFamilyType {
  return (IVY_FAMILY_TYPES as readonly string[]).includes(type);
}

export interface RobotIvyMetadata {
  settings: {
    volume: number;
  };
  telemetry: {
    battery_level: number;
    is_charging: boolean;
    sdk_version: string;
    rssi: number;
    link_speed: number;
    ssid: string;
    wifi_state: number;
    memory_free: number;
    memory_total: number;
    memory_used_percent: number;
    storage_free: number;
    storage_total: number;
    uptime: number;
    connection_state: number;
    missed_heartbeats: number;
    last_heartbeat_seconds: number;
  };
}

export interface RobotIvy extends BaseDevice {
  /**
   * Type discriminator for this category. Always `robot`.
   */
  category: 'robot';

  /**
   * Type discriminator for this type: the Ivy family's head or a rebadge.
   *
   * @see {@link IVY_FAMILY_TYPES}
   */
  type: IvyFamilyType;

  /**
   * The metadata associated with this device.
   *
   * @see {@link RobotIvyMetadata}
   */
  metadata: RobotIvyMetadata;
}

export interface RobotOzzyMetadata {
  /**
   * The volume of the device.
   */
  volume: number;

  /**
   * The battery level of the device.
   */
  batteryLevel: number;
}

export interface RobotOzzy extends BaseDevice {
  /**
   * Type discriminator for this category. Always `robot`.
   */
  category: 'robot';

  /**
   * Type discriminator for this type. Always `ozzy`.
   */
  type: 'ozzy';

  /**
   * The metadata associated with this device.
   *
   * @see {@link RobotOzzyMetadata}
   */
  metadata: RobotOzzyMetadata;
}

export interface RobotOzzyAndroid extends BaseDevice {
  category: 'robot';

  type: 'ozzyAndroid';

  metadata: RobotOzzyMetadata;
}

/**
 * The supported devices for the category `robot`.
 */
export type DeviceRobot = RobotIvy | RobotOzzy | RobotOzzyAndroid;

/**
 * Represents a device.
 */
export type Device = DeviceRobot;

/**
 * Metadata for an access point device as seen from the dashboard.
 * The tenant device endpoint serializes metadata as a raw dict (untyped T),
 * so keys arrive in snake_case from MongoDB, not camelCase.
 */
export interface AccessPointMetadata {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  serial_number: string | null;
}

/**
 * Represents an access point device in the tenant context (dashboard view).
 */
export interface TenantAccessPoint extends BaseDevice {
  category: 'access-point';
  type: 'mini' | 'maxi';
  metadata: AccessPointMetadata;
  /** Access points have no device sessions — always null. */
  latestSession: null;
}

/**
 * Display labels per device category and type.
 *
 * The robot map is typed against `Device['type']`, so adding a model to that
 * union without naming it here is a compile error rather than a device that
 * renders as a raw slug in one view and correctly in another.
 */
export const DEVICE_TYPE_LABELS_BY_CATEGORY: {
  robot: Record<Device['type'], string>;
  'access-point': Record<TenantAccessPoint['type'], string>;
} = {
  robot: {
    ivy: 'Ivy',
    lily: 'Lily',
    ozzy: 'Ozzy',
    ozzyAndroid: 'Ozzy (Android)',
  },
  'access-point': {
    mini: 'Mini',
    maxi: 'Maxi',
  },
};

/** Flat type -> label lookup across every device category. */
export const DEVICE_TYPE_LABELS: Record<string, string> = {
  ...DEVICE_TYPE_LABELS_BY_CATEGORY.robot,
  ...DEVICE_TYPE_LABELS_BY_CATEGORY['access-point'],
};

/**
 * Extends a {@link Device} type with an `attachedTo` property.
 *
 * Use this utility type to associate a device with an entity (e.g. `robot`)
 *
 * @template TDevice A device type that extends the base `Device` type.
 * @template TAttachment The type of entity the device is attached to.
 */
export type DeviceWithAttachment<TDevice extends Device, TAttachment> = TDevice & {
  attachedTo: TAttachment;
};

/**
 * WebSocket event payload for device connection open event.
 * Sent when a device connects and a session opens.
 */
export interface DeviceConnectionOpenPayload {
  /**
   * The unique ID of the device.
   */
  deviceId: string;

  /**
   * The system device ID.
   */
  systemDeviceId: string;

  /**
   * The hardware ID of the device.
   */
  hardwareId: string;

  /**
   * The session ID for this connection.
   */
  sessionId: string;

  /**
   * Connection status. Always 'open' for this event.
   */
  status: 'open';

  /**
   * When the device connected (session start time).
   */
  connectedAt: string;

  /**
   * Timestamp of the event.
   */
  timestamp: string;
}

/**
 * WebSocket event payload for device connection closed event.
 * Sent when a device disconnects and a session closes.
 */
export interface DeviceConnectionClosedPayload {
  /**
   * The unique ID of the device.
   */
  deviceId: string;

  /**
   * The system device ID.
   */
  systemDeviceId: string;

  /**
   * The hardware ID of the device.
   */
  hardwareId: string;

  /**
   * The session ID for this connection.
   */
  sessionId: string;

  /**
   * Connection status. Always 'closed' for this event.
   */
  status: 'closed';

  /**
   * When the device originally connected (session start time).
   */
  connectedAt: string;

  /**
   * When the device disconnected (session end time).
   */
  disconnectedAt: string;

  /**
   * Duration of the session in seconds.
   */
  durationSeconds: number;

  /**
   * Reason for disconnection (e.g., idle_timeout, server_stop, error).
   */
  reason: string;

  /**
   * Timestamp of the event.
   */
  timestamp: string;
}

/**
 * Union type for device connection events (open and closed).
 */
export type DeviceConnectionPayload =
  | DeviceConnectionOpenPayload
  | DeviceConnectionClosedPayload;

/**
 * WebSocket event payload for device telemetry updates.
 * Contains real-time telemetry data from the device.
 */
export interface DeviceTelemetryPayload {
  /**
   * The unique ID of the device.
   */
  deviceId: string;

  /**
   * The system device ID.
   */
  systemDeviceId: string;

  /**
   * The hardware ID of the device.
   */
  hardwareId: string;

  /**
   * Telemetry data from the device.
   */
  telemetry: {
    batteryLevel?: number;
    isCharging?: boolean;
    sdkVersion?: string;
    rssi?: number;
    linkSpeed?: number;
    ssid?: string;
    wifiState?: number;
    memoryFree?: number;
    memoryTotal?: number;
    memoryUsedPercent?: number;
    storageFree?: number;
    storageTotal?: number;
    uptime?: number;
    connectionState?: number;
    missedHeartbeats?: number;
    lastHeartbeatSeconds?: number;
  };

  /**
   * Timestamp of the telemetry event.
   */
  timestamp: string;
}

/**
 * Union type for all WebSocket event payloads.
 */
export type DeviceWebSocketPayload = DeviceConnectionPayload | DeviceTelemetryPayload;
